// The three tiles at the top of Cash Planning: the balance and where it comes
// from, the next fourteen days, and whether payroll can be paid from committed
// money. Every number is cashplan_page_v1's; the tiles only lay it out.
import { Landmark, CalendarRange, ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '../../lib/shadcn/button'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  balanceSourceLabel, coverVerdict, formatDay, formatDayYear, formatZar, formatZarShort,
  type CashPlan, type CoverLine,
} from '../../utils/cashPlan'

function Tile({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="min-w-0 rounded-lg border border-border bg-card p-4">
      <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {icon} {title}
      </h2>
      {children}
    </section>
  )
}

function Row({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3 text-sm', className)}>
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium tabular-nums text-foreground">{value}</span>
    </div>
  )
}

function CoverRow({ title, line }: { title: string; line: CoverLine | null }) {
  const v = coverVerdict(line)
  return (
    <div className="space-y-1 border-t border-border pt-2 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className="font-medium text-foreground">{title}{line ? `, ${formatDay(line.date)}` : ''}</span>
        <span className="tabular-nums">{line ? formatZar(line.amount_zar) : ''}{line?.estimate ? ' (estimate)' : ''}</span>
      </div>
      <Chip tone={v.tone} className="whitespace-normal">{v.text}</Chip>
      {line && !line.known && line.needs && <p className="text-xs text-muted-foreground">Needs: {line.needs}</p>}
      {line?.estimate && line.basis && <p className="text-xs text-muted-foreground">{line.basis}</p>}
      {line && (line.unknown_out_before ?? 0) > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {line.unknown_out_before} outflow{line.unknown_out_before === 1 ? '' : 's'} with an unknown amount fall before this and are not in the balance.
        </p>
      )}
    </div>
  )
}

export function TopStrip({ plan, onEnterBalance }: { plan: CashPlan; onEnterBalance: () => void }) {
  const b = plan.balance
  const n = plan.next14
  const c = plan.payroll_cover
  const stale = b.days_old > 3
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      <Tile icon={<Landmark className="h-4 w-4" />} title="Balance">
        <p className={cn('text-2xl font-semibold tabular-nums', b.amount_zar < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-foreground')}>
          {formatZar(b.amount_zar)}
        </p>
        <p className="text-xs text-muted-foreground">{balanceSourceLabel(b)}</p>
        {stale && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
            {b.days_old} days old. Everything after {formatDayYear(b.as_at)} is projected, not seen on a statement.
          </p>
        )}
        <div className="mt-3 space-y-1">
          <Row label="Projected today" value={formatZar(b.projected_today_zar)} />
          {b.entry && !b.entry.in_use && (
            <Row label="Entered balance, not in use" value={`${formatZar(b.entry.amount_zar)} at ${formatDay(b.entry.as_at)}`} />
          )}
          {b.source === 'entry' && b.statement_to && (
            <Row label={`Last statement, ${formatDay(b.statement_to)}`} value={formatZar(b.statement_closing_zar)} />
          )}
        </div>
        <Button className="mt-3" size="sm" variant="outline" onClick={onEnterBalance}>Enter balance as at</Button>
      </Tile>

      <Tile icon={<CalendarRange className="h-4 w-4" />} title="Next 14 days">
        {n ? (
          <div className="space-y-1">
            <Row label="In, received and earned" value={formatZar(n.in_committed_zar)} />
            <Row label={<span className="italic">In, forecast</span>} value={<span className="italic text-muted-foreground">{formatZar(n.in_forecast_zar)}</span>} />
            <Row label="Out, known" value={formatZar(n.out_zar)} />
            {n.unknown_out > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Plus {n.unknown_out} outflow{n.unknown_out === 1 ? '' : 's'} with an unknown amount, not in these totals.
              </p>
            )}
            <div className="mt-2 border-t border-border pt-2">
              <Row
                label={`Lowest balance, ${formatDay(n.lowest_projected_date)}`}
                value={<span className={n.lowest_projected_zar < plan.settings.buffer_zar ? 'text-rose-700 dark:text-rose-300' : ''}>{formatZar(n.lowest_projected_zar)}</span>}
              />
              <Row
                label={`Without forecast, ${formatDay(n.lowest_committed_date)}`}
                value={formatZar(n.lowest_committed_zar)}
                className="text-xs"
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">The balance is later than the next fourteen days.</p>
        )}
      </Tile>

      <Tile icon={<ShieldCheck className="h-4 w-4" />} title="Payroll cover">
        <p className="mb-2 text-xs text-muted-foreground">
          From the balance plus Received and Earned Uber money only. Forecast money is never counted here.
        </p>
        <div className="space-y-2">
          <CoverRow title="EMP201" line={c.emp201} />
          <CoverRow title="Salaries" line={c.salary} />
        </div>
        <div className="mt-3 space-y-1 border-t border-border pt-2">
          <Row label="Committed money" value={formatZar(c.committed_funds_zar)} />
          <Row
            label="Months of cover"
            value={c.months_of_cover === null ? 'Cannot tell' : `${c.months_of_cover}${c.emp201_included ? '' : ', salaries only'}`}
          />
          {c.monthly_payroll_zar !== null && <Row label="One month of payroll" value={formatZarShort(c.monthly_payroll_zar)} className="text-xs" />}
        </div>
      </Tile>
    </div>
  )
}
